import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Select, Option, ImgWrapper, SpinnerIcon } from '@sistemiv/s-components'
import { OrgType } from '../../layout/TopNav'
import { imageBase } from '../../../services/http-common'
import { useMyOrganizations } from '../../../repositories/organizations/organizations-my.respository'
import OrganizationPermissionsSection from '../organization-permissions/OrganizationPermissionsSection'
import { useUserAppRoles } from '../../../repositories/users/user-app-roles.repository'
import Organizations from '../../organizations/Organizations'
import { mapMyOrganizations } from './MyOrganizationInfo.utils'
import { useAuth } from '../../../hooks/useAuth'

const MyAccountOrganizationInfo = ({ showTitle = false }: { showTitle?: boolean }) => {
  const { userId } = useAuth()
  const { t } = useTranslation()
  const [selectedOrganization, setSelectedOrganization] = React.useState<OrgType | null>(null)
  const { data: organizationsPages, isLoading: organizationsLoading } = useMyOrganizations({
    userId,
    enabled: true,
  })

  const organizations = mapMyOrganizations(organizationsPages?.organizations)

  const { data: appRoles, isLoading: appRolesLoading } = useUserAppRoles({
    userId,
    organizationId: selectedOrganization?.code,
  })
  const { groups, units, status, applications } = appRoles || {}

  React.useEffect(() => {
    if (organizations.length > 0 && !selectedOrganization) {
      setSelectedOrganization(organizations[0])
    }
  }, [organizations, selectedOrganization])

  return (
    <div className='space-y-5 relative w-full h-full min-h-[200px]'>
      {/* loading */}
      {organizationsLoading || appRolesLoading ? (
        <div className='w-full flex justify-center inset-x-auto absolute top-10 left-1/2 -translate-x-1/2'>
          <SpinnerIcon className='w-6 h-6 text-sky-500' />
        </div>
      ) : // empty state
      !organizations.length ? (
        <Organizations type='section' />
      ) : (
        <div className='space-y-5'>
          {showTitle && <p className='text-base text-gray-800 font-semibold'>{t('organization')}</p>}
          <Select
            type='organization'
            value={selectedOrganization ?? undefined}
            disabled={organizations.length <= 1}
            onChange={(e) => {
              setSelectedOrganization(e)
            }}
          >
            {organizations
              .filter((o) => o.code !== selectedOrganization?.code)
              .map((o) => (
                <Option key={o.code} value={o}>
                  <div className='pb-1 hover:bg-blue-50 cursor-pointer flex'>
                    <ImgWrapper
                      src={`${imageBase}/${o.code}/76`}
                      className='w-6 h-6 rounded-full mr-2 flex items-center justify-center border border-gray-300'
                      onErrorComponent={
                        <p className='text-md text-white w-6 h-6 mr-2 rounded-full flex items-center justify-center bg-[#7b87ae]'>
                          {o.name.charAt(0).toUpperCase()}
                        </p>
                      }
                    />
                    <span> {o.name}</span>
                  </div>
                </Option>
              ))}
          </Select>

          {appRoles && (
            <OrganizationPermissionsSection
              status={status || ''}
              apps={applications || []}
              groups={groups || []}
              units={units || []}
            />
          )}
        </div>
      )}
    </div>
  )
}

export default MyAccountOrganizationInfo
